import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Rating,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TrendingUp, Building2 } from 'lucide-react';
import Button from '../Button';
import { colors } from '../../config/colors';

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  border: `2px solid ${colors.gray[200]}`,
  overflow: 'hidden',
  minWidth: 0,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    borderColor: colors.primary[500],
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2.5rem',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
  },
}));

/**
 * BrokerCard - Displays a broker with details and actions
 * @param {Object} broker - { id, name, logo, color }
 * @param {Object} details - { rating, activeClients, brokerage, features, description }
 * @param {string} logoGradient - CSS gradient for logo background
 * @param {Function} onViewDetails - (brokerName) => void
 * @param {Function} onCompare - (brokerName) => void
 */
const BrokerCard = ({
  broker,
  details,
  logoGradient,
  onViewDetails,
  onCompare,
}) => {
  return (
    <StyledCard>
      {/* Card Header with Logo */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[50]}, ${colors.secondary[50]})`,
          p: 3,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <LogoContainer sx={{ background: logoGradient }}>
          {broker.logo}
        </LogoContainer>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 'bold',
            color: colors.primary[700],
            mb: 1,
          }}
        >
          {broker.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <Rating
            value={details.rating}
            precision={0.1}
            readOnly
            size="small"
            sx={{
              '& .MuiRating-iconFilled': {
                color: '#fbbf24',
              },
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
            {details.rating}
          </Typography>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, p: 3, minWidth: 0, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            minHeight: 40,
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
        >
          {details.description}
        </Typography>

        {/* Stats */}
        <Box sx={{ mb: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minWidth: 0 }}>
            <TrendingUp size={18} color={colors.primary[500]} style={{ flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 0 }}>
              {details.activeClients} Active Clients
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Building2 size={18} color={colors.secondary[500]} style={{ flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 0 }}>
              Brokerage: {details.brokerage}
            </Typography>
          </Box>
        </Box>

        {/* Features Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {details.features.map((feature, index) => (
            <Chip
              key={index}
              label={feature}
              size="small"
              sx={{
                backgroundColor: colors.primary[50],
                color: colors.primary[700],
                fontWeight: 'bold',
                fontSize: '0.75rem',
                '&:hover': {
                  backgroundColor: colors.primary[100],
                },
              }}
            />
          ))}
        </Box>
      </CardContent>

      {/* Card Actions */}
      <CardActions sx={{ p: 2, pt: 0, gap: 1, flexDirection: { xs: 'row', sm: 'row' } }}>
        <Button
          variant="primary"
          size="sm"
          className="flex-1"
          onClick={() => onViewDetails(broker.name)}
        >
          View Details
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => onCompare(broker.name)}
        >
          Compare
        </Button>
      </CardActions>
    </StyledCard>
  );
};

export default BrokerCard;
